import React, {ReactNode, useMemo} from "react";
import {colors} from "patient-ping-remedy/packages/theme";
import Icon, {ICONS} from "patient-ping-remedy/packages/icon";
import {css} from "@emotion/css";
import DropdownMultiGroupSelect from "patient-ping-remedy/packages/dropdown-multi-group";
import {LabelSettingTypography} from "patient-ping-remedy/packages/typography";
import DatePicker from "patient-ping-remedy/packages/date-picker";
import {FilterResponse} from "@/api/dto/filters";
import FilterDrawer from "@common/FilterDrawer";
import {
  Wrapper,
  Trigger,
  FilterWrapper,
  FilterLabel,
  FilterBadge,
  FilterDatePickerWrapper,
  FilterDropdownMultiGroupSelectWrapper,
  FilterDropdownSingleSelectWrapper
}
  from "@common/FilterComponent.styles";
import DropdownList from "patient-ping-remedy/packages/dropdown-list";

export interface nonGroupedValues {
  id: string;
  label: string;
  value: string;
  quickFilter?: boolean;
}

export interface groupedValues extends nonGroupedValues {
  group: string;
}

export interface groupedOption {
  label: string;
  value: string;
  options: groupedValues[];
}

export interface dateRange {
    startDate: Date | null;
    endDate: Date | null;
}

interface BaseFilterOption {
  label: string;
  key: string;
  hasQuickFilter?: boolean;
}

export interface DefaultFilterOption extends BaseFilterOption {
  dataType: 'string' | 'array' | 'date';
}

interface AjaxFilterOption extends BaseFilterOption {
  dataType: 'ajax';
  query: FilterQuery;
}

type FilterDataType = DefaultFilterOption | AjaxFilterOption;

export type MultiselectSearchFilterOption = FilterDataType & {
  type: 'multiselect-search';
  options: nonGroupedValues[] | groupedOption[];
}

export type SingleSelectFilterOption = FilterDataType & {
    type: 'single-select';
    options: nonGroupedValues[];
}

export type DateRangeFilterOption = FilterDataType & {
  type: 'date-range';
  endLabel: string;
}

export type FilterOption = MultiselectSearchFilterOption | SingleSelectFilterOption | DateRangeFilterOption;

export type FilterInvalidate = (rosterId?: number) => void;

export type FilterQuery = {
  params: Record<string, unknown>;
  invalidate: FilterInvalidate;
}

export type FilterArgs = {
  filterOptions: FilterOption[];
  currentFilters: object;
}

type Props = {
  isOpen: boolean;
  handleOpen(isOpen: boolean): void;
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  filterFunction: Function;
  filterArgs: FilterArgs;
  savedFilter?: FilterResponse;
  savableFilters?: boolean;
  pinnedFilters?: boolean;
  pinnedCount?: number;
  pinnedLabel?: string;
  initialFilters?: Record<string, unknown>;
  onSave?(filter: FilterResponse): void;
  onReset?(filters: object): void;
}

const countFiltersApplied = (filters: object): number => {
  return Object.values(filters).reduce((accumulator, currentValue) => {
    if (Array.isArray(currentValue)) {
      return accumulator + currentValue.length;
    }
    if (currentValue && typeof currentValue === 'object') {
      if (currentValue.id && currentValue.label && currentValue.value) {
        return accumulator + 1;
      }

      return accumulator + (Object.values(currentValue).filter(Boolean).length);
    }
    return accumulator;
  }, 0);
};

const FilterComponent = (props: Props) => {
  const filtersApplied = useMemo(() => {
    return countFiltersApplied(props.filterArgs.currentFilters);
  }, [props.filterArgs.currentFilters]);

  const filterLabel = () => {
    if(filtersApplied === 0) return 'Filter';
    return `${filtersApplied} ${filtersApplied > 1 ? 'filters' : 'filter'} applied`;
  };

  const handleChange = (e: nonGroupedValues[] | groupedValues[], key: string) => {
    props.filterFunction(e, key);
  };

  const filterContent = () => {
    return props.filterArgs?.filterOptions.map((option, index) => {
      const currentFilter = props.filterArgs.currentFilters[option.key as keyof typeof props.filterArgs.currentFilters];
      const count = countFiltersApplied({ [option.key]: currentFilter });

      let label = option.label;
      let content: ReactNode;

      if (option.type === 'multiselect-search') {
        content = (
          <FilterDropdownMultiGroupSelectWrapper>
            <DropdownMultiGroupSelect
              id={`${option.label}-${index}`}
              label=""
              options={option.options}
              value={currentFilter || []}
              menuPlacement="auto"
              handleChange={(e) => handleChange(e!, option.key)}
            />
          </FilterDropdownMultiGroupSelectWrapper>
        );
      } else if (option.type === 'single-select') {
        content = (
          <FilterDropdownSingleSelectWrapper>
            <DropdownList
              id={`${option.label}-${index}`}
              label=""
              items={option.options}
              value={currentFilter || null}
              menuPlacement="auto"
              isClearable={true}
              handleChange={(e: nonGroupedValues | groupedValues | null) => {
                if (e) {
                  handleChange([e], option.key);
                } else {
                  handleChange([], option.key);
                }
              }}
            />
          </FilterDropdownSingleSelectWrapper>
        );
      } else if (option.type === 'date-range') {
        const dateRangeFilter = currentFilter as {startDate: Date, endDate: Date};

        label = `${option.label} & ${option.endLabel}`;
        content = (
          <FilterDatePickerWrapper>
            <DatePicker
              id={option.label.replace(/\s+/g, '-').toLowerCase()}
              value={dateRangeFilter?.startDate || null}
              maxDate={dateRangeFilter?.endDate}
              onChange={(date: Date | null) => {
                const currentRange = dateRangeFilter || {};
                props.filterFunction({ ...currentRange, startDate: date }, option.key);
                return date;
              }}
            />
            <DatePicker
              id={option.endLabel.replace(/\s+/g, '-').toLowerCase()}
              value={dateRangeFilter?.endDate || null}
              minDate={dateRangeFilter?.startDate}
              onChange={(date: Date | null) => {
                const currentRange = dateRangeFilter || {};
                props.filterFunction({ ...currentRange, endDate: date }, option.key);
                return date;
              }}
            />
          </FilterDatePickerWrapper>
        );
      }

      return (
        <FilterWrapper
          key={option.key}
          className="filter-wrapper"
          headerItems={[
            <FilterLabel key="label">
              {label}
              {count > 0 && <FilterBadge backgroundColor={colors.gray4}>{count}</FilterBadge>}
            </FilterLabel>
          ]}
        >
          {content}
        </FilterWrapper>
      );
    });
  };

  return (
    <Wrapper>
      <Trigger data-testid="filter-trigger" onClick={() => props.handleOpen(true)}>
        <Icon iconClass={ICONS.filter} color={colors.purple3} className={css({ fontSize: '1.4rem' })} />
        <LabelSettingTypography className={css({ marginLeft: '5px' })}>
          {filterLabel()}
        </LabelSettingTypography>
      </Trigger>
      <FilterDrawer
        isOpen={props.isOpen}
        close={() => props.handleOpen(false)}
        id={props.id}
        filterArgs={props.filterArgs}
        filtersApplied={filtersApplied}
        savedFilter={props.savedFilter}
        savableFilters={props.savableFilters}
        pinnedFilters={props.pinnedFilters}
        pinnedCount={props.pinnedCount}
        pinnedLabel={props.pinnedLabel}
        initialFilters={props.initialFilters}
        onSave={props.onSave}
        onReset={props.onReset}
      >
        {filterContent()}
      </FilterDrawer>
    </Wrapper>
  );
};

export default React.memo(FilterComponent);
